/**
 * User-parameter aspect — Phase 5.1 PR 5.1.b.
 *
 * Registers `BLOCKUSERPARAMETER` / `ACDBBLOCKUSERPARAMETER` as a
 * `DynamicAspect<UserParameter, ParseCtx, WriteCtx>` so the registry
 * dispatcher can locate the user-parameter parser/writer without the
 * existing `if (canonical === "ACDBBLOCKUSERPARAMETER")` branch.
 *
 * Like the grip aspect (5.1.a), this PR is **purely additive**: the
 * existing dispatcher in `dxfBridge.dynamic.parser.ts` keeps calling
 * `parseUserParameter` directly. The aspect is verified through its
 * dedicated unit test.
 *
 * Unlike the grip aspect, user parameters carry **side-channel
 * context**:
 *   - Parse: `Map<handle, AssocVariable>` so the parser can resolve
 *     the post-marker `330` reference to its `AssocVariable` payload.
 *   - Write: `{ nextHandle, blockRecordHandle }` so the writer can
 *     mint a new handle when the synthetic parameter has none yet.
 *
 * Both contexts are first-class generic parameters of `DynamicAspect`
 * so callers see typed `ctx`.
 */

import type { AssocVariable } from "@dxf-ai-editor/dxf-io";

import type { UserParameter } from "./dynamicBlockParameters";
import type { DynamicAspect } from "./dxfBridge.dynamic.aspect";
import { registerAspect } from "./dxfBridge.dynamic.aspect";
import { parseUserParameter } from "./dxfBridge.dynamic.parser.userParam";
import {
  writeUserParameter,
  type UserParamWriterContext,
} from "./dxfBridge.dynamic.writer.userParam";

/**
 * Canonical class names recognised by the user-parameter aspect.
 * Mirrors the matcher in `dxfBridge.dynamic.parser.ts` so the
 * registry-driven dispatcher can land on the aspect for both AutoCAD
 * spellings (un-prefixed pre-2018 + prefixed 2018+).
 */
export const USER_PARAMETER_CLASSES: ReadonlySet<string> = new Set([
  "ACDBBLOCKUSERPARAMETER",
]);

export type UserParameterParseCtx = Map<string, AssocVariable>;

export const USER_PARAMETER_ASPECT: DynamicAspect<
  UserParameter,
  UserParameterParseCtx,
  UserParamWriterContext
> = {
  id: "user-parameter",
  classes: USER_PARAMETER_CLASSES,
  matches(canonicalType) {
    return USER_PARAMETER_CLASSES.has(canonicalType);
  },
  parse(obj, _canonicalType, assocVariables) {
    return parseUserParameter(obj, assocVariables);
  },
  write(param, ctx) {
    return writeUserParameter(param, ctx);
  },
};

registerAspect(USER_PARAMETER_ASPECT);
